import React, { useState, useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { getEdgeColor } from '../../context/DeckContext';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import EDGE_INDEXES from '../../../../../../constants/edgeTypeIndexex';
import { getResponsiveEdgeWidth } from '../../../../../../constants/edgeWidth';

const EdgeIndicator = ({
	position,
	label,
	type,
	length,
	onSelectEdge,
	isSelected,
	parentIsSelected,
}) => {
	const [edgeWidth, setEdgeWidth] = useState(getResponsiveEdgeWidth());

	useEffect(() => {
		const handleResize = () => {
			setEdgeWidth(getResponsiveEdgeWidth());
		};

		window.addEventListener('resize', handleResize);
		window.addEventListener('orientationchange', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('orientationchange', handleResize);
		};
	}, []);

	const getPositionStyles = () => {
		const offset = 0;
		switch (position) {
			case 'top':
				return {
					top: `-${offset}px`,
					left: 0,
					right: 0,
					height: edgeWidth,
					zIndex: EDGE_INDEXES[type],
				};
			case 'right':
				return {
					top: 0,
					bottom: 0,
					right: `-${offset}px`,
					width: edgeWidth,
					zIndex: EDGE_INDEXES[type],
				};
			case 'bottom':
				return {
					bottom: `-${offset}px`,
					left: 0,
					right: 0,
					height: edgeWidth,
					zIndex: EDGE_INDEXES[type],
				};
			case 'left':
				return {
					top: 0,
					bottom: 0,
					left: `-${offset}px`,
					width: edgeWidth,
					zIndex: EDGE_INDEXES[type],
				};
			default:
				return {};
		}
	};

	const getTooltipContent = () => {
		switch (position) {
			case 'top':
				return `Top Edge: ${label} - ${length} inch(es)`;
			case 'right':
				return `Right Edge: ${label} - ${length} inch(es)`;
			case 'bottom':
				return `Bottom Edge: ${label} - ${length} inch(es)`;
			case 'left':
				return `Left Edge: ${label} - ${length} inch(es)`;
			default:
				return `Edge: ${label} - ${length} inch(es)`;
		}
	};

	const getTooltipPlacement = () => {
		switch (position) {
			case 'top':
				return 'top';
			case 'right':
				return 'right';
			case 'bottom':
				return 'bottom';
			case 'left':
				return 'left';
			default:
				return 'top';
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			onSelectEdge(label);
		}
	};

	return (
		<Tooltip
			title={getTooltipContent()}
			arrow
			placement={getTooltipPlacement()}
		>
			<Box
				onPointerDown={(e) => {
					e.stopPropagation();
				}}
				onTouchStart={(e) => {
					e.stopPropagation();
				}}
				onPointerUp={(e) => {
					e.stopPropagation();
					e.preventDefault();
					onSelectEdge(label);
				}}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				role="button"
				aria-label={`Select ${label} edge`}
				sx={{
					position: 'absolute',
					...getPositionStyles(),
					backgroundColor: getEdgeColor(type, isSelected),
					cursor: 'pointer',
					transition: 'background-color 0.3s, transform 0.1s',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					touchAction: 'manipulation',
				}}
			>
				{parentIsSelected && position == 'left' && (
					<ArrowLeftIcon sx={{ color: 'white', fontSize: edgeWidth * 1.5 }} />
				)}
				{parentIsSelected && position == 'right' && (
					<ArrowRightIcon sx={{ color: 'white', fontSize: edgeWidth * 1.5 }} />
				)}
				{parentIsSelected && position == 'top' && (
					<ArrowDropUpIcon sx={{ color: 'white', fontSize: edgeWidth * 1.5 }} />
				)}
				{parentIsSelected && position == 'bottom' && (
					<ArrowDropDownIcon
						sx={{ color: 'white', fontSize: edgeWidth * 1.5 }}
					/>
				)}
			</Box>
		</Tooltip>
	);
};

EdgeIndicator.propTypes = {
	position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']).isRequired,
	label: PropTypes.oneOf(['Top', 'Left', 'Right', 'Bottom']).isRequired,
	type: PropTypes.string.isRequired,
	length: PropTypes.number.isRequired,
	onSelectEdge: PropTypes.func.isRequired,
	isSelected: PropTypes.bool.isRequired,
	parentIsSelected: PropTypes.bool.isRequired,
};

export default EdgeIndicator;
